import { useState, useEffect, useCallback } from 'react';
import { UnifiedMessagingService } from '@/services/api/unified-messaging.service';

interface OfflineMessage {
  id: string;
  content: string;
  messageType: string;
  messageData?: Record<string, unknown>;
  replyToId?: string;
  timestamp: number;
  retryCount: number;
}

interface UseOfflineMessagesProps {
  conversationId: string | null;
  isOnline: boolean;
}

export function useOfflineMessages({ conversationId, isOnline }: UseOfflineMessagesProps) {
  const [offlineMessages, setOfflineMessages] = useState<OfflineMessage[]>([]);
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastConnectionStatus, setLastConnectionStatus] = useState(navigator.onLine);

  const queueOfflineMessage = useCallback((messageData: {
    content: string;
    messageType: string;
    messageData?: Record<string, unknown>;
    replyToId?: string;
  }) => {
    const offlineMessage: OfflineMessage = {
      id: `offline_${Date.now()}_${Math.random()}`,
      ...messageData,
      timestamp: Date.now(),
      retryCount: 0
    };
    
    setOfflineMessages(prev => [...prev, offlineMessage]);
  }, []);

  const retryOfflineMessages = useCallback(async () => {
    if (offlineMessages.length === 0 || isRetrying || !conversationId) return;
    
    setIsRetrying(true);
    
    const messagesToRetry = [...offlineMessages];
    setOfflineMessages([]);
    
    for (const offlineMessage of messagesToRetry) {
      try {
        const response = await UnifiedMessagingService.getInstance().sendUnifiedMessage({
          conversation_id: conversationId,
          content: offlineMessage.content,
          message_type: offlineMessage.messageType as any,
          message_data: offlineMessage.messageData,
          reply_to_id: offlineMessage.replyToId
        });
        
        if (response.success) {
          // Message sent successfully
        } else {
          console.error('❌ Failed to retry offline message:', offlineMessage.id, response.error);
          // Re-queue with increased retry count
          if (offlineMessage.retryCount < 3) {
            setOfflineMessages(prev => [...prev, {
              ...offlineMessage,
              retryCount: offlineMessage.retryCount + 1
            }]);
          }
        }
      } catch (error) {
        console.error('❌ Error retrying offline message:', offlineMessage.id, error);
        // Re-queue with increased retry count
        if (offlineMessage.retryCount < 3) {
          setOfflineMessages(prev => [...prev, {
            ...offlineMessage,
            retryCount: offlineMessage.retryCount + 1
          }]);
        }
      }
      
      // Small delay between retries
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setIsRetrying(false);
  }, [offlineMessages, isRetrying, conversationId]);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => {
      setLastConnectionStatus(true);

      // Retry offline messages when connection is restored
      if (offlineMessages.length > 0) {
        setTimeout(retryOfflineMessages, 1000); // Small delay to ensure connection is stable
      }
    };
    
    const handleOffline = () => {
      setLastConnectionStatus(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [offlineMessages.length, retryOfflineMessages]);

  // Retry offline messages when connection is restored
  useEffect(() => {
    if (!isOnline || offlineMessages.length === 0) return;
    
    const interval = setInterval(retryOfflineMessages, 1000); // Retry every second
    return () => clearInterval(interval);
  }, [isOnline, offlineMessages.length, retryOfflineMessages]);

  return {
    offlineMessages,
    isRetrying,
    queueOfflineMessage,
    retryOfflineMessages
  };
}

